import { type PaletteKey, type PixelCanvas, sprite } from '../../../lib';
import { box, byRow, geometry, outlined } from '../effect-guns.sprite';

/**
 * Overclock (the Glitch set's gun, ADR-0160 amended): a rail pistol in matte black. A segmented neon strip runs its
 * length (stock, body, the channel between its rails) and a light chases along it, a segment a frame; a heat sink of
 * stepped fins stands on its back, a magenta warning light behind them; two rails close on the muzzle. On a shot
 * (`fire`) the whole strip flashes white and the fins glow magenta with the heat, then cool.
 */

/** Where the strip starts: its segments are 4 px (3 lit, a dark gap), counted from here. */
const STRIP_X = 7;

/**
 * The strip's tone at column `x` (row `top` its upper row): the chase's head bright, the segment behind it lit, the rest
 * dim; `heat` 3 all white (the shot), 2 lit, 1 cooling.
 */
function strip(x: number, frame: number, heat: number, top: boolean): PaletteKey {
  const k = x - STRIP_X;
  if (k % 4 === 3) return heat === 3 ? 'l' : 'G';
  if (heat === 3) return top ? 'w' : 'l';
  if (heat === 2) return top ? (x > 40 ? 'w' : 'l') : 'g';
  const phase = (((Math.floor(k / 4) - frame) % 4) + 4) % 4;
  const lit = heat === 1 ? Math.min(phase, 1) + 1 : phase;
  return top ? (lit === 0 ? 'w' : lit === 1 ? 'l' : 'g') : lit === 0 ? 'l' : lit === 1 ? 'g' : 'G';
}

const MATTE = ['k', '1', '2', '3'] as const;

const overclock = (frame: number, heat = 0) =>
  outlined((c: PixelCanvas) => {
    // The stock: matte black, lit along its top, the strip down its middle.
    box(c, 5, 16, 22, 24, [2, 1], byRow(16, 24, MATTE, 1, 2), 'l');
    c.hline(7, 14, 16, '4');
    for (let x = STRIP_X; x <= 21; x++) c.set(x, 20, strip(x, frame, heat, true));
    // The heat sink: fins stepping up to the middle, lit on their left, hot at their tips on a shot.
    const tip: PaletteKey = heat === 3 ? 'w' : heat === 2 ? 'p' : heat === 1 ? 'P' : '4';
    for (const [x, top] of [
      [26, 10],
      [30, 8],
      [34, 8],
      [38, 10],
    ] as const) {
      c.vline(x, top, 12, '3').vline(x + 1, top, 12, '2');
      c.set(x, top, tip).set(x + 1, top, heat ? tip : '3');
      if (heat >= 2) c.set(x, top + 1, 'p').set(x + 1, top + 1, heat === 3 ? 'p' : 'P');
    }
    // The body: a slab, its front's top cut back.
    box(c, 22, 13, 46, 26, [2, 1], byRow(13, 26, MATTE, 1, 2), 'r');
    c.hline(24, 31, 13, '4');
    // The strip along its side, two rows.
    for (let x = 23; x <= 45; x++) {
      c.set(x, 19, strip(x, frame, heat, true));
      c.set(x, 20, strip(x, frame, heat, false));
    }
    // A magenta line under it, bright while it is hot.
    c.hline(26, 44, 24, heat >= 2 ? 'p' : 'P');
    // The warning light behind the fins.
    c.set(23, 16, heat ? 'w' : 'p').set(24, 16, heat ? 'p' : 'P');
    // The trigger.
    box(c, 31, 27, 34, 29, [0, 1], (_x, y) => (y === 27 ? '2' : '1'), 'r');
    // Two rails closing on the muzzle, the strip running on between them.
    for (let x = 47; x <= 61; x++) {
      const k = (x - 47) / 14;
      const top = Math.round(14.5 + k * 3);
      const bottom = Math.round(25 - k * 3);
      c.set(x, top, '3')
        .set(x, top + 1, '2')
        .set(x, bottom, 'k')
        .set(x, bottom - 1, '1');
      for (let y = top + 2; y < bottom - 1; y++) c.set(x, y, 'k');
      if (x < 60) c.set(x, 19, strip(x, frame, heat, true)).set(x, 20, strip(x, frame, heat, false));
    }
    // The emitter at its tip.
    c.set(62, 19, heat ? 'w' : 'l').set(62, 20, heat ? 'l' : 'g');
  });

export default sprite({
  name: 'pollinator_glitch',
  ...geometry,
  notes: `Overclock (ADR-0160 amended, the Glitch set's gun): a rail pistol in matte black, a segmented neon strip down
its length (stock, body, between the rails) with a light chasing along it a segment a frame, a heat sink of four stepped
fins on its back, a magenta warning light, two rails closing on a lime emitter at the muzzle. On a shot (fire) the strip
flashes white and the fins glow magenta, then cool over 3 frames; a magenta line under the body brightens with them.
Its flair: sparks off the fins, the emitter's charge. Rubric (4×): S4 R4 P4 L4 C4 A4 F4. Weakest: the body is a plain
slab (the fins and the strip carry it); at 1:1 the chase reads as a flicker along a lime line.`,
  anims: {
    idle: { fps: 8, frames: [overclock(0), overclock(1), overclock(2), overclock(3)] },
    fire: { fps: 20, loop: false, frames: [overclock(0, 3), overclock(0, 2), overclock(0, 1), overclock(0)] },
  },
});
